import React, { useEffect, useState, useRef } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Building2, Upload, Mail, Phone, User, Edit3 } from 'lucide-react';

const emptyForm = { nome: '', email: '', telefono: '', referente: '', note: '' };

export const AgenziaCompagnie = () => {
  const [compagnie, setCompagnie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [uploadingLogoFor, setUploadingLogoFor] = useState(null);
  const fileInputRef = useRef(null);
  const logoTargetId = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const fetchCompagnie = async () => {
    try {
      const res = await api.get('/agenzia/compagnie');
      setCompagnie(res.data || []);
    } catch {
      toast.error('Errore nel caricamento compagnie');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCompagnie(); }, []);

  const openNew = () => { setEditId(null); setForm({ ...emptyForm }); setFormOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({ nome: c.nome || '', email: c.email || '', telefono: c.telefono || '', referente: c.referente || '', note: c.note || '' });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.nome.trim()) { toast.error('Il nome compagnia e\' obbligatorio'); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.patch(`/agenzia/compagnie/${editId}`, form);
        toast.success('Compagnia aggiornata');
      } else {
        await api.post('/agenzia/compagnie', form);
        toast.success('Compagnia creata');
      }
      setFormOpen(false);
      fetchCompagnie();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const triggerLogoUpload = (compagniaId) => {
    logoTargetId.current = compagniaId;
    fileInputRef.current?.click();
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    const compagniaId = logoTargetId.current;
    if (!file || !compagniaId) return;
    setUploadingLogoFor(compagniaId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/agenzia/compagnie/${compagniaId}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo caricato');
      fetchCompagnie();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nel caricamento del logo');
    } finally {
      setUploadingLogoFor(null);
      e.target.value = '';
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <input type="file" ref={fileInputRef} className="hidden" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogoChange} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Compagnie Agenzia</h1>
          <p className="text-sm text-slate-500">Loghi e recapiti per l'invio dei fogli cassa</p>
        </div>
        <Button onClick={openNew} data-testid="agenzia-nuova-compagnia">
          <Plus size={16} className="mr-2" /> Nuova Compagnia
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Caricamento...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {compagnie.map((c) => (
            <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-3" data-testid={`agenzia-compagnia-card-${c.id}`}>
              <div className="flex items-center gap-3">
                <button onClick={() => triggerLogoUpload(c.id)}
                  className="w-14 h-14 rounded-lg border border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50 hover:border-slate-400 shrink-0"
                  title="Carica logo">
                  {uploadingLogoFor === c.id ? (
                    <span className="text-[10px] text-slate-400">...</span>
                  ) : c.logo_data_url ? (
                    <img src={c.logo_data_url} alt={c.nome} className="w-full h-full object-contain" />
                  ) : (
                    <Upload size={16} className="text-slate-400" />
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">{c.nome}</p>
                  <p className="text-xs text-slate-400">Clicca sul logo per caricarlo</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => openEdit(c)}><Edit3 size={16} /></Button>
              </div>
              <div className="text-sm text-slate-600 space-y-1">
                <p className="flex items-center gap-2"><Mail size={13} className="text-slate-400" />{c.email || <span className="text-amber-600">email non impostata</span>}</p>
                <p className="flex items-center gap-2"><Phone size={13} className="text-slate-400" />{c.telefono || '-'}</p>
                <p className="flex items-center gap-2"><User size={13} className="text-slate-400" />{c.referente || '-'}</p>
              </div>
            </div>
          ))}
          {compagnie.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-400 bg-white rounded-xl border border-slate-200">
              <Building2 className="mx-auto mb-2" size={28} />
              Nessuna compagnia. Aggiungine una per iniziare.
            </div>
          )}
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editId ? 'Modifica Compagnia' : 'Nuova Compagnia'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input value={form.nome} onChange={(e) => set('nome', e.target.value)} />
            </div>
            <div>
              <Label>Email (per invio fogli cassa)</Label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input value={form.telefono} onChange={(e) => set('telefono', e.target.value)} />
            </div>
            <div>
              <Label>Referente</Label>
              <Input value={form.referente} onChange={(e) => set('referente', e.target.value)} />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => set('note', e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setFormOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Salvataggio...' : 'Salva'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
